import { useFragmentQuery } from '@app/modules/fragments/hooks/useFragment';
import { DeliveryStart } from '@app/modules/fragments/screens/view/components/export/external-export/DeliveryStart';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

/**
 * Props for the ContentAiFragmentDeliveryStart component
 */
interface Props {
  /** The unique identifier of the fragment to enable delivery for */
  fragmentId: string;
}

/**
 * ContentAiFragmentDeliveryStart Component
 *
 * A component that provides a collapsible interface for enabling external delivery
 * for a specific content fragment. This component renders a button that, when clicked,
 * expands to show the delivery activation interface.
 *
 * The component features:
 * - A toggle button with a dropdown arrow icon
 * - Collapsible delivery start interface
 * - Automatic cache invalidation for delivery status queries
 * - Integration with the fragment query system
 * - Internationalized button text
 *
 * When the button is clicked, it reveals the DeliveryStart component which handles
 * the actual delivery activation process. The component automatically hides the
 * delivery interface after successful activation.
 *
 * @param props - Component props
 * @param props.fragmentId - The unique identifier of the fragment to enable delivery for
 *
 * @returns A React component that renders either a toggle button or the delivery start interface
 *
 * @example
 * ```tsx
 * <ContentAiFragmentDeliveryStart fragmentId="fragment-123" />
 * ```
 */
export function ContentAiFragmentDeliveryStart({ fragmentId }: Props) {
  const { formatMessage } = useIntl();
  const fragmentQuery = useFragmentQuery(fragmentId);
  const [show, setShow] = useState(false);

  return (
    <Box>
      {!show && (
        <Button
          size="small"
          color="inherit"
          variant="outlined"
          startIcon={<KeyboardArrowDownIcon />}
          onClick={() => setShow(true)}
        >
          {formatMessage({
            id: 'fragments.deliveryStatus.enable',
            defaultMessage: 'Activar export externo',
          })}
        </Button>
      )}
      {show && fragmentQuery.data && (
        <Box mt={2}>
          <DeliveryStart
            fragment={fragmentQuery.data!}
            onStarted={() => {
              setShow(false);
            }}
          />
        </Box>
      )}
    </Box>
  );
}
